import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, help, value, onChange, testid }) => (
  <label className="block">
    <span className="text-xs font-semibold text-slate-200">{label}</span>
    <div className="relative mt-1">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-num">$</span>
      <Input type="number" inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)}
        className="pl-7 bg-[#05070D] border-[#1A2338] font-num h-11" data-testid={testid} />
    </div>
    <span className="text-[11px] text-slate-500 block mt-1">{help}</span>
  </label>
);

export const BudgetDialog = ({ open, onOpenChange, account, onSaved }) => {
  const [budget, setBudget] = useState("100");
  const [bet, setBet] = useState("5");
  const [limit, setLimit] = useState("20");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open && account) {
      setBudget(String(account.start_balance));
      setBet(String(account.rules.bet_size));
      setLimit(String(account.rules.daily_loss_limit));
      setTouched(false);
    }
  }, [open, account]);

  const changeBudget = (v) => {
    setBudget(v);
    if (!touched && Number(v) > 0) {
      setBet(String(Math.max(1, Math.round(Number(v) * 0.05))));
      setLimit(String(Math.max(1, Math.round(Number(v) * 0.2))));
    }
  };

  const save = async () => {
    try {
      await axios.post(`${API}/bot/budget`, { budget: Number(budget), bet_size: Number(bet), daily_loss_limit: Number(limit) });
      toast.success(`Budget set to $${budget}. Practice account restarted.`);
      onOpenChange(false);
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Check the numbers — budget at least $5, bet $1–$100");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0B1220] border-[#1A2338] text-slate-100 max-w-md" data-testid="budget-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-xl">How much are you willing to put in?</DialogTitle>
          <DialogDescription className="text-slate-400">
            Only use money you can afford to lose. The practice bot will trade with this amount so you can see how it would really go.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <Field label="Total budget" help="Everything you'd put into Polymarket for this." value={budget} onChange={changeBudget} testid="budget-total-input" />
          <Field label="Per bet" help="Tip: keep each bet around 5% of your budget so a losing streak doesn't wipe you out." value={bet}
            onChange={(v) => { setBet(v); setTouched(true); }} testid="budget-bet-input" />
          <Field label="Daily loss limit" help="Stop betting for the day after losing this much. Around 20% of your budget is common." value={limit}
            onChange={(v) => { setLimit(v); setTouched(true); }} testid="budget-limit-input" />
          <p className="text-[11px] text-amber-300/90">Saving restarts the practice account with your new budget (old practice trades are cleared).</p>
          <button onClick={save} className="w-full py-3 rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] text-[#03141A] font-semibold transition-colors" data-testid="budget-save-btn">
            Save my budget
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
